/**
 * EngagementsDashboard Component
 * Feature 014: Service Provider Engagements
 *
 * Two-column dashboard showing Pending and Accepted engagements
 * Replaces the placeholder in the SP Dashboard Engagements tab
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { Users, AlertCircle, Loader2, ArrowUpDown } from 'lucide-react';
import {
  useProviderConsultationRequests,
  useAcceptConsultationRequest,
  useDeclineConsultationRequest,
  useUpdateConsultationNotes,
} from '../hooks/useConsultations';
import { EngagementCard } from './EngagementCard';
import { DeclineEngagementModal } from './DeclineEngagementModal';
import type { ConsultationRequest } from '../types';

type SortOrder = 'newest' | 'oldest';

export function EngagementsDashboard() {
  const navigate = useNavigate();
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest');
  const [decliningEngagement, setDecliningEngagement] = useState<ConsultationRequest | null>(null);

  // Fetch all engagements (uses current user from JWT token)
  const { data: allEngagements, isLoading, error } = useProviderConsultationRequests();

  // Mutations
  const acceptMutation = useAcceptConsultationRequest();
  const declineMutation = useDeclineConsultationRequest();
  const updateNotesMutation = useUpdateConsultationNotes();

  // Split engagements by status
  const { pendingEngagements, acceptedEngagements } = useMemo(() => {
    if (!allEngagements) {
      return { pendingEngagements: [], acceptedEngagements: [] };
    }

    const pending = allEngagements.filter((e) => e.status === 'PENDING');
    const accepted = allEngagements.filter((e) => e.status === 'ACCEPTED');

    // Sort based on sortOrder
    const sortFn = (a: ConsultationRequest, b: ConsultationRequest) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    };

    return {
      pendingEngagements: [...pending].sort(sortFn),
      acceptedEngagements: [...accepted].sort(sortFn),
    };
  }, [allEngagements, sortOrder]);

  // Handlers
  const handleAccept = async (id: string) => {
    try {
      await acceptMutation.mutateAsync(id);
    } catch (err) {
      console.error('Failed to accept engagement:', err);
    }
  };

  const handleDeclineClick = (engagement: ConsultationRequest) => {
    setDecliningEngagement(engagement);
  };

  const handleDeclineConfirm = async (reason: string) => {
    if (!decliningEngagement) return;

    try {
      await declineMutation.mutateAsync({
        id: decliningEngagement.id,
        data: { reason },
      });
      setDecliningEngagement(null);
    } catch (err) {
      console.error('Failed to decline engagement:', err);
    }
  };

  const handleUpdateNotes = async (id: string, notes: string) => {
    try {
      await updateNotesMutation.mutateAsync({
        id,
        data: { providerNotes: notes },
      });
    } catch (err) {
      console.error('Failed to update notes:', err);
    }
  };

  const handleViewConversation = (careSeekerUserId: string) => {
    // Navigate to messages with care seeker pre-selected
    navigate(`/messages?userId=${careSeekerUserId}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#026857]" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load engagements. Please try refreshing the page.
        </AlertDescription>
      </Alert>
    );
  }

  const hasNoEngagements = pendingEngagements.length === 0 && acceptedEngagements.length === 0;

  if (hasNoEngagements) {
    return (
      <Card className="p-12 text-center">
        <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-xl text-gray-900 mb-2">No Consultation Requests Yet</h3>
        <p className="text-gray-600">
          When families request consultations or message you, they'll appear here.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Sort */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-gray-900">Consultations</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest')}
        >
          <ArrowUpDown className="w-4 h-4 mr-2" />
          {sortOrder === 'newest' ? 'Newest First' : 'Oldest First'}
        </Button>
      </div>

      {/* Two Column Layout */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Pending Engagements */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Pending Consultation Requests ({pendingEngagements.length})
            </h3>
          </div>

          {pendingEngagements.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-gray-600">No pending consultation requests</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {pendingEngagements.map((engagement) => (
                <EngagementCard
                  key={engagement.id}
                  engagement={engagement}
                  onAccept={handleAccept}
                  onDecline={() => handleDeclineClick(engagement)}
                  onViewConversation={handleViewConversation}
                  isAccepting={acceptMutation.isPending}
                  isDeclining={declineMutation.isPending}
                />
              ))}
            </div>
          )}
        </div>

        {/* Accepted Engagements */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Accepted Consultation Requests ({acceptedEngagements.length})
            </h3>
          </div>

          {acceptedEngagements.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-gray-600">No accepted consultation requests</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {acceptedEngagements.map((engagement) => (
                <EngagementCard
                  key={engagement.id}
                  engagement={engagement}
                  onUpdateNotes={handleUpdateNotes}
                  onViewConversation={handleViewConversation}
                  isUpdatingNotes={updateNotesMutation.isPending}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Decline Modal */}
      {decliningEngagement && (
        <DeclineEngagementModal
          open={!!decliningEngagement}
          onOpenChange={(open) => !open && setDecliningEngagement(null)}
          careSeekerName={`${decliningEngagement.careSeeker?.firstName} ${decliningEngagement.careSeeker?.lastName}`}
          onConfirm={handleDeclineConfirm}
          isSubmitting={declineMutation.isPending}
        />
      )}
    </div>
  );
}
